import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { policies, policyOrder } from "@/content/policies";
import { Reveal } from "@/components/Reveal";

export default function PolicyPage({ slug: slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const policy = policies[slug];

  useEffect(() => {
    document.title = policy ? `${policy.title} — ORIVA AI` : "Page not found — ORIVA AI";
  }, [policy]);

  if (!policy) {
    return (
      <main className="mx-auto max-w-3xl px-5 pb-32 pt-40 sm:px-8" data-testid="policy-not-found">
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-900">
          Page not found
        </h1>
        <p className="mt-4 text-base text-slate-600">
          The page you're looking for doesn't exist.
        </p>
        <Link
          to="/"
          data-testid="policy-back-home"
          className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-oriva-blue underline-offset-4 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to homepage
        </Link>
      </main>
    );
  }

  return (
    <main className="bg-white pb-32 pt-32 sm:pt-40" data-testid={`policy-page-${slug}`}>
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <Link
            to="/"
            data-testid="policy-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to homepage
          </Link>
          <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
            {policy.updated}
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            {policy.title}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600">{policy.intro}</p>
        </Reveal>

        <div className="mt-12 flex flex-col gap-10">
          {policy.sections.map((section, i) => (
            <Reveal key={section.heading} delay={Math.min(i * 0.04, 0.2)}>
              <section data-testid={`policy-section-${i}`}>
                <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph, p) => (
                  <p key={p} className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                    {paragraph}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <nav className="mt-16 rounded-2xl border border-slate-200 bg-slate-50 p-7" data-testid="policy-nav">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
              Other policies
            </p>
            <ul className="mt-4 flex flex-col gap-2.5">
              {policyOrder
                .filter((item) => item.slug !== slug)
                .map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={item.path}
                      data-testid={`policy-nav-${item.slug}`}
                      className="text-sm font-medium text-oriva-blue underline-offset-4 hover:underline"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>
        </Reveal>
      </div>
    </main>
  );
}
